<template>
  <v-row no-gutters class="pt-5">
    <v-col class="pr-2" v-if="isFetched">
      <v-form ref="quickCheckForm" v-model="isValid">
        <card-title-row translation-path="sidebar.waterModelingQuality.title" class="pb-5" :has-buttons="false">
          <template #subtitle>
            <italic-subheader
              style="font-size: 12px !important;"
              class="text-left"
              translation-path="sidebar.waterModelingQuality.subtitle"
            />
          </template>
        </card-title-row>
        <sidebar-row border-bottom class="pb-5">
          <v-row no-gutters class="pb-5">
            <v-col cols="7">
              <data-input v-bind="modelingProfileInput" v-model="modelingProfile" />
            </v-col>
          </v-row>
        </sidebar-row>
        <sidebar-row class="pb-5">
          <v-row no-gutters class="justify-space-between" align="center">
            <v-col cols="5" class="text-body-2 center-content text-left">
              {{ $i18n.t('sidebar.waterModelingQuality.waterQualityParameter') }}:
            </v-col>
            <v-col cols="6">
              <data-input v-bind="waterQualityParameterInput" v-model="waterQualityParameter" />
            </v-col>
          </v-row>
        </sidebar-row>
        <template v-if="hasJunctionSelection">
          <sidebar-row class="pb-3">
            <v-tooltip bottom :disabled="junctionsLayersAdded && !isJunctionSelected" max-width="300">
              <template #activator="{ on }">
                <span v-on="on">
                  <data-input
                    simple
                    ref="junctionSelection"
                    v-bind="junctionSelectionInput"
                    @pushFeatures="selectJunction"
                  />
                </span>
              </template>
              {{ junctionSelectionTooltip }}
            </v-tooltip>
          </sidebar-row>
          <sidebar-row v-if="isJunctionSelected" class="pb-3">
            <ribbon-list :listItems="computedSelectedJunctionsList" styleType="raw">
              <template #itemButtons-prepend="{ item }">
                <v-icon @click="deleteFeature(item.value)" color="rgba(0,0,0,.7)" size="19px">mdi-close</v-icon>
              </template>
            </ribbon-list>
          </sidebar-row>
        </template>
      </v-form>
    </v-col>
    <main-loader v-else />
  </v-row>
</template>
<script>
import { call, get, sync } from 'vuex-pathify';

import CardTitleRow from '@/components/CardTitleRow';
import DataInput from '@/components/DataInput';
import ItalicSubheader from '@/components/ItalicSubheader';
import MainLoader from '@/components/MainLoader';
import RibbonList from '@/components/RibbonList';
import SidebarRow from '@/components/SidebarRow';

import profiles from '@/mixins/profiles';

export default {
  name: 'WaterModelingQuality',
  components: { CardTitleRow, DataInput, ItalicSubheader, MainLoader, RibbonList, SidebarRow },
  mixins: [profiles],
  computed: {
    epanetProfiles: get('epanet/epanetProfiles'),
    netType() {
      return this.$route.params.netType;
    },

    hasJunctionSelection() {
      return this.waterQualityParameter === 'trace';
    },

    projectElements: get('layers/project@layers'),
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers;
    },
    junctionsLayers() {
      return this.projectLayers.filter(layer => layer.data_source_name === 'water_junctions');
    },
    junctionsLayersAdded() {
      return this.junctionsLayers?.length > 0;
    },

    selectedJunctions: get('map/selectedFeatures@:currentJunctionsLayerId'),
    computedSelectedJunctions() {
      return this.selectedJunctions || [];
    },
    computedSelectedJunctionsList() {
      return this.computedSelectedJunctions.map(junction => {
        return { value: junction, title: `ID: ${junction}` };
      });
    },
    selectedJunction() {
      return this.computedSelectedJunctions[0] || undefined;
    },
    isJunctionSelected() {
      return this.selectedJunction ? true : false;
    },

    junctionSelectionTooltip() {
      return this.junctionsLayersAdded
        ? this.$i18n.t('sidebar.waterModelingQuality.onlyOneJunction')
        : this.$i18n.t('sidebar.waterModelingQuality.junctionsNotAdded');
    },
    junctionSelectionInput() {
      return {
        name: 'junction_selection',
        translationPath: 'sidebar.waterModelingQuality.selectJunction',
        dataType: { name: 'features' },
        disabled: !this.junctionsLayersAdded || this.isJunctionSelected,
        featuresSettings: {
          layers: {
            included: [...this.junctionsLayers?.map(layer => layer.id)],
          },
        },
        subtitleTranslationPath: 'sidebar.waterModelingQuality.selectJunctionSubtitle',
      };
    },
    modelingProfileInput() {
      return {
        label: this.$i18n.t('sidebar.waterModelingQuality.modelingProfile'),
        dataType: { name: 'select' },
        items: this.epanetProfiles,
        itemText: 'name',
        returnObject: true,
        rules: [v => !!v || v === 0 || this.$i18n.t('rules.required')],
        hideDetails: true,
      };
    },
    waterQualityParameterInput() {
      return {
        dataType: { name: 'select' },
        items: [
          { text: this.$i18n.t('sidebar.waterModelingQuality.params.age'), value: 'age' },
          { text: this.$i18n.t('sidebar.waterModelingQuality.params.chemical'), value: 'chemical' },
          { text: this.$i18n.t('sidebar.waterModelingQuality.params.trace'), value: 'trace' },
        ],
        rules: [v => !!v || ''],
        hideDetails: true,
      };
    },

    isStickyButtonsLoading: sync('sidebar/isStickyButtonsLoading'),
    isStickyButtonsValid: sync('sidebar/isStickyButtonsValid'),
    isStickyButtonsVisible: sync('sidebar/isStickyButtonsVisible'),
    stickyButtons: sync('sidebar/stickyButtons'),
  },
  data: () => ({
    currentJunctionsLayerId: null,
    isFetched: false,
    isValid: false,
    modelingProfile: undefined,
    waterQualityParameter: '',
  }),
  methods: {
    getEpanetProfiles: call('epanet/getEpanetProfiles'),
    runModel: call('epanet/runModel'),
    deleteFeature(id, layerId = this.currentJunctionsLayerId) {
      this.$root.$emit('setProjectLayerStyle', this.currentJunctionsLayerId);
      this.currentJunctionsLayerId = null;
      const deletedIds = id ? [id] : [];
      this.$store.set('map/DELETE_SELECTED_FEATURES!', {
        type: 'selectedFeatures',
        features: {
          [layerId]: deletedIds,
        },
      });
    },
    async runModeling() {
      this.isStickyButtonsLoading = true;
      try {
        const payload = this.getPayload();
        const r = await this.runModel(payload);
        this.$router.push({
          name: 'modelingResultsScenario',
          params: {
            netType: 'water',
            scenarioId: r.scenario_id,
          },
        });
        localStorage.setItem(`scenarioId-water`, r.scenario_id);
      } finally {
        this.isStickyButtonsLoading = false;
      }
    },
    selectJunction(features) {
      if (!features || features.length === 0) {
        return;
      }
      const featureId = features[0].id;
      this.currentJunctionsLayerId = features[0].layerId;
      this.$refs[`junctionSelection`]?.$refs['data-input']?.toggleOff();
      this.$store.set('map/ADD_SELECTED_FEATURES!', {
        type: 'selectedFeatures',
        features: {
          [this.currentJunctionsLayerId]: [featureId],
        },
      });
      this.$root.$emit('setProjectLayerStyle', this.currentJunctionsLayerId, {
        selectedColors: { selectedColor: '#F00', haloColorSelected: '#F00' },
      });
    },
    getPayload() {
      const modelingProfile = this.getWaterModelingProfilePayload(this.modelingProfile);
      const payload = {
        ...modelingProfile,
        override_options: {
          QUALITY: this.waterQualityParameter.toLocaleUpperCase(),
          ...(this.hasJunctionSelection ? { 'TRACE NODE': `J_${this.selectedJunction}` } : {}),
        },
      };
      return payload;
    },
    async init() {
      this.isFetched = false;
      try {
        await this.getEpanetProfiles();
        this.$root.$off('runQualityModeling');
        this.$root.$on('runQualityModeling', this.runModeling);
        this.stickyButtons = [
          {
            action: 'runQualityModeling',
            translationPath: 'dialog.agreeModel',
          },
        ];
        this.isStickyButtonsLoading = false;
        this.isStickyButtonsVisible = true;
        this.isFetched = true;
      } catch {
        //
      }
    },
  },
  watch: {
    hasJunctionSelection(nV) {
      if (!nV) {
        this.deleteFeature();
      }
    },
  },
  mounted() {
    this.$watch(
      vm => {
        return { formValid: vm.isValid, hasSelection: vm.hasJunctionSelection, selectedFeature: vm.selectedJunction };
      },
      nV => {
        const { formValid, hasSelection, selectedFeature } = nV;
        this.isStickyButtonsValid = formValid && (!hasSelection || selectedFeature) ? true : false;
      }
    );
    this.init();
  },
  beforeDestroy() {
    if (this.selectedJunction) this.deleteFeature(this.selectedJunction);
    this.isStickyButtonsVisible = false;
    this.stickyButtons = [];
  },
};
</script>
